import { useEffect, useState } from 'react';
import { Card, Modal, FormField, TextInput, Toggle, StatusBadge, ConfirmDialog, EmptyState, PageHeader } from '@/components/ui';
import { Icon } from '@/components/ui/Icon';
import { superApi } from '@/shared/api/superApi';
import { toast } from '@/components/ui/Toast';

interface Plan {
  id: number;
  code: string;
  name: string;
  monthly_price_cents: number;
  minutes_included: number;
  overage_price_per_minute_cents?: number;
  ai_transcript_price_per_minute_cents?: number;
  ai_summary_price_per_call_cents?: number;
  max_agents: number;
  is_popular?: boolean;
  active?: boolean;
}

const emptyForm = { code: '', name: '', monthly_price_cents: 9900, minutes_included: 1000, overage_price_per_minute_cents: 0, ai_transcript_price_per_minute_cents: 0, ai_summary_price_per_call_cents: 0, max_agents: 5, is_popular: false };

export function Plans() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Plan | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  useEffect(() => { void load(); }, []);

  async function load() {
    setLoading(true);
    try {
      setPlans((await superApi.plans()) as Plan[]);
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }

  async function create() {
    if (!form.code || !form.name) {
      toast('יש למלא קוד ושם', 'warn');
      return;
    }
    try {
      await superApi.createPlan(form);
      toast('החבילה נוצרה', 'ok');
      setShow(false);
      setForm({ ...emptyForm });
      void load();
    } catch {
      toast('שגיאה ביצירת החבילה', 'danger');
    }
  }

  async function saveEdit() {
    if (!editing) return;
    try {
      await superApi.updatePlan(editing.id, {
        name: editing.name,
        monthly_price_cents: editing.monthly_price_cents,
        minutes_included: editing.minutes_included,
        overage_price_per_minute_cents: editing.overage_price_per_minute_cents ?? 0,
        ai_transcript_price_per_minute_cents: editing.ai_transcript_price_per_minute_cents ?? 0,
        ai_summary_price_per_call_cents: editing.ai_summary_price_per_call_cents ?? 0,
        max_agents: editing.max_agents,
        is_popular: editing.is_popular,
      });
      toast('נשמר', 'ok');
      setEditing(null);
      void load();
    } catch {
      toast('שגיאה בשמירה', 'danger');
    }
  }

  async function togglePopular(p: Plan) {
    try {
      await superApi.updatePlan(p.id, { is_popular: !p.is_popular });
      void load();
    } catch {
      toast('שגיאה בעדכון', 'danger');
    }
  }

  async function remove(p: Plan) {
    try {
      await superApi.deletePlan(p.id);
      toast('החבילה הושבתה', 'ok');
      void load();
    } catch {
      toast('שגיאה בהשבתת החבילה', 'danger');
    }
  }

  return (
    <div className="stack" style={{ padding: 'var(--gap)' }}>
      <PageHeader title="תוכניות מחיר" actions={<>
        <button type="button" className="btn primary" onClick={() => setShow(true)}>+ חבילה חדשה</button>
      </>} />

      {loading ? (
        <Card>טוען…</Card>
      ) : loadError && plans.length === 0 ? (
        <Card>
          <EmptyState
            icon="warn"
            title="שגיאה בטעינת החבילות"
            description="לא ניתן היה לטעון את תוכניות המחיר מהשרת."
            action={<button type="button" className="btn primary" onClick={() => void load()}>נסה שוב</button>}
          />
        </Card>
      ) : plans.length === 0 ? (
        <Card>
          <EmptyState icon="info" title="אין עדיין חבילות" description="צור חבילת מחיר ראשונה."
            action={<button type="button" className="btn primary" onClick={() => setShow(true)}>+ חבילה חדשה</button>} />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--gap)' }}>
          {plans.map((p) => (
            <Card key={p.id}>
              <div className="stack">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <strong>{p.name}</strong>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    {p.is_popular && <StatusBadge tone="accent" size="sm">פופולרי</StatusBadge>}
                    {p.active === false && <StatusBadge tone="neutral" size="sm">לא פעיל</StatusBadge>}
                  </div>
                </div>
                <div style={{ fontSize: 'var(--t-display)', fontWeight: 700, color: 'var(--accent)' }}>
                  ₪{(p.monthly_price_cents / 100).toFixed(0)}<small style={{ fontSize: 12, color: 'var(--muted)' }}>/חודש</small>
                </div>
                <div style={{ color: 'var(--text-2)', fontSize: 'var(--t-sm)', display: 'grid', gap: 4 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="phone" size={14} color="var(--muted)" /> {p.minutes_included} דקות כלולות
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="bolt" size={14} color="var(--muted)" />
                    {(p.overage_price_per_minute_cents ?? 0) > 0
                      ? <>‏₪{((p.overage_price_per_minute_cents ?? 0) / 100).toFixed(2)} לדקה עודפת</>
                      : 'ללא חיוב על דקות עודפות'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="ai" size={14} color="var(--muted)" />
                    {((p.ai_transcript_price_per_minute_cents ?? 0) > 0 || (p.ai_summary_price_per_call_cents ?? 0) > 0)
                      ? <>‏₪{((p.ai_transcript_price_per_minute_cents ?? 0) / 100).toFixed(2)} לדקת תמלול · ₪{((p.ai_summary_price_per_call_cents ?? 0) / 100).toFixed(2)} לסיכום</>
                      : 'AI כלול (ללא חיוב נוסף)'}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Icon name="users" size={14} color="var(--muted)" /> עד {p.max_agents} נציגים
                  </div>
                  <div dir="ltr" style={{ color: 'var(--muted)', fontSize: 'var(--t-xs)' }}>code: {p.code}</div>
                </div>
                <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                  <button type="button" className="btn" onClick={() => setEditing({ ...p })}>
                    <Icon name="edit" size={13} /> ערוך
                  </button>
                  <button type="button" className="btn" onClick={() => togglePopular(p)}>
                    <Icon name="star" size={13} /> {p.is_popular ? 'הסר פופולרי' : 'סמן פופולרי'}
                  </button>
                  <button type="button" className="btn danger" aria-label="השבתת חבילה" onClick={() => setConfirmDelete(p)}>
                    <Icon name="trash" size={13} />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={show} onClose={() => setShow(false)} title="חבילה חדשה"
        footer={<><button type="button" className="btn" onClick={() => setShow(false)}>ביטול</button><button type="button" className="btn primary" onClick={create}>שמור</button></>}>
        <div className="stack">
          <FormField label="קוד" required><TextInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} dir="ltr" /></FormField>
          <FormField label="שם" required><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></FormField>
          <FormField label="מחיר חודשי (אגורות)"><TextInput type="number" value={String(form.monthly_price_cents)} onChange={(e) => setForm({ ...form, monthly_price_cents: Number(e.target.value) })} dir="ltr" /></FormField>
          <FormField label="דקות כלולות"><TextInput type="number" value={String(form.minutes_included)} onChange={(e) => setForm({ ...form, minutes_included: Number(e.target.value) })} dir="ltr" /></FormField>
          <FormField label="מחיר לדקה עודפת (אגורות)" hint="חיוב על כל דקת שיחה מעבר לדקות הכלולות. 0 = ללא חיוב"><TextInput type="number" value={String(form.overage_price_per_minute_cents)} onChange={(e) => setForm({ ...form, overage_price_per_minute_cents: Number(e.target.value) })} dir="ltr" /></FormField>
          <FormField label="מחיר AI — דקת תמלול (אגורות)" hint="חיוב אוטומטי על כל דקת שיחה שתומללה בפועל. 0 = כלול"><TextInput type="number" value={String(form.ai_transcript_price_per_minute_cents)} onChange={(e) => setForm({ ...form, ai_transcript_price_per_minute_cents: Number(e.target.value) })} dir="ltr" /></FormField>
          <FormField label="מחיר AI — סיכום שיחה (אגורות)" hint="חיוב אוטומטי על כל שיחה שסוכמה בפועל. 0 = כלול"><TextInput type="number" value={String(form.ai_summary_price_per_call_cents)} onChange={(e) => setForm({ ...form, ai_summary_price_per_call_cents: Number(e.target.value) })} dir="ltr" /></FormField>
          <FormField label="מכסת נציגים"><TextInput type="number" value={String(form.max_agents)} onChange={(e) => setForm({ ...form, max_agents: Number(e.target.value) })} dir="ltr" /></FormField>
          <Toggle checked={form.is_popular} onChange={(v) => setForm({ ...form, is_popular: v })} label="חבילה פופולרית" />
        </div>
      </Modal>

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} title={`עריכת ${editing?.name ?? ''}`}
        footer={<><button type="button" className="btn" onClick={() => setEditing(null)}>ביטול</button><button type="button" className="btn primary" onClick={saveEdit}>שמור</button></>}>
        {editing && (
          <div className="stack">
            <FormField label="שם"><TextInput value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></FormField>
            <FormField label="קוד"><TextInput value={editing.code} disabled dir="ltr" /></FormField>
            <FormField label="מחיר חודשי (אגורות)"><TextInput type="number" value={String(editing.monthly_price_cents)} onChange={(e) => setEditing({ ...editing, monthly_price_cents: Number(e.target.value) })} dir="ltr" /></FormField>
            <FormField label="דקות כלולות"><TextInput type="number" value={String(editing.minutes_included)} onChange={(e) => setEditing({ ...editing, minutes_included: Number(e.target.value) })} dir="ltr" /></FormField>
            <FormField label="מחיר לדקה עודפת (אגורות)" hint="חיוב על כל דקת שיחה מעבר לדקות הכלולות. 0 = ללא חיוב"><TextInput type="number" value={String(editing.overage_price_per_minute_cents ?? 0)} onChange={(e) => setEditing({ ...editing, overage_price_per_minute_cents: Number(e.target.value) })} dir="ltr" /></FormField>
            <FormField label="מחיר AI — דקת תמלול (אגורות)" hint="חיוב אוטומטי על כל דקת שיחה שתומללה בפועל. 0 = כלול"><TextInput type="number" value={String(editing.ai_transcript_price_per_minute_cents ?? 0)} onChange={(e) => setEditing({ ...editing, ai_transcript_price_per_minute_cents: Number(e.target.value) })} dir="ltr" /></FormField>
            <FormField label="מחיר AI — סיכום שיחה (אגורות)" hint="חיוב אוטומטי על כל שיחה שסוכמה בפועל. 0 = כלול"><TextInput type="number" value={String(editing.ai_summary_price_per_call_cents ?? 0)} onChange={(e) => setEditing({ ...editing, ai_summary_price_per_call_cents: Number(e.target.value) })} dir="ltr" /></FormField>
            <FormField label="מכסת נציגים"><TextInput type="number" value={String(editing.max_agents)} onChange={(e) => setEditing({ ...editing, max_agents: Number(e.target.value) })} dir="ltr" /></FormField>
            <Toggle checked={!!editing.is_popular} onChange={(v) => setEditing({ ...editing, is_popular: v })} label="חבילה פופולרית" />
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        onConfirm={() => confirmDelete && remove(confirmDelete)}
        title="השבתת חבילה"
        message={`להשבית את "${confirmDelete?.name}"? לקוחות קיימים לא יושפעו, אך לא ניתן יהיה להירשם אליה.`}
        confirmLabel="השבת"
        tone="danger"
      />
    </div>
  );
}
